import { startRegistration } from '@simplewebauthn/browser';
import { useEffect, useMemo, useState } from 'react';
import { begin_registration, finish_registration } from '../api/kernel_client';
import { CeremonyChrome } from '../components/ceremony_chrome';
import { get_translations } from '../i18n/translations';
import { probe_passkey_support } from '../lib/capability_probe';
import { read_redirect_params } from '../lib/url_params';

/**
 * /redirect/register — mobile / popup-blocked / Capacitor variant of
 * popup_register. On success redirects to
 * `<return_origin>/sso/callback?credential_id=...&state=...` (or the
 * deep-link scheme equivalent for Capacitor).
 */
export function RedirectRegisterRoute() {
  const t = useMemo(() => get_translations(), []);
  const params = useMemo(() => read_redirect_params(window.location.search), []);
  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const [supported, set_supported] = useState<boolean | null>(null);
  const [state, set_state] = useState<
    'idle' | 'running' | 'success' | { kind: 'error'; message: string }
  >('idle');
  const [label, set_label] = useState<string>(search.get('label') ?? 'Passkey');

  useEffect(() => {
    void probe_passkey_support().then((ok) => set_supported(ok));
  }, []);

  async function run_ceremony() {
    if (params === null) return;
    set_state('running');
    try {
      const auth_json = search.get('authorization') ?? '';
      let parsed: unknown = { type: 'session' };
      if (auth_json !== '') {
        try {
          parsed = JSON.parse(atob(auth_json));
        } catch {
          parsed = { type: 'session' };
        }
      }
      const opts = await begin_registration({
        label,
        authorization: parsed as Parameters<typeof begin_registration>[0]['authorization'],
      });
      const credential = await startRegistration({
        optionsJSON: opts.public_key as unknown as Parameters<
          typeof startRegistration
        >[0]['optionsJSON'],
      });
      const result = await finish_registration({
        challenge_id: opts.challenge_id,
        credential,
        label,
      });
      set_state('success');
      const target = build_callback_url(params.return_origin, {
        credential_id: result.credential.id,
        state: params.state,
      });
      window.location.replace(target);
    } catch (e) {
      const message = e instanceof Error ? e.message : t.error_unknown;
      const target = build_callback_url(params.return_origin, {
        error: message,
        state: params.state,
      });
      set_state({ kind: 'error', message });
      window.setTimeout(() => window.location.replace(target), 1500);
    }
  }

  if (params === null) {
    return <CeremonyChrome title={t.app_title} body={<p>Invalid launch parameters.</p>} />;
  }
  if (supported === false) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.error_no_support}</p>} />;
  }
  if (supported === null) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.waiting}</p>} />;
  }

  return (
    <CeremonyChrome
      title={t.app_title}
      body={
        <>
          <label style={{ display: 'block', marginBottom: '12px', color: '#1a1a1a' }}>
            {t.register_label}
            <input
              type="text"
              value={label}
              onChange={(e) => set_label(e.target.value)}
              disabled={state === 'running'}
              style={{
                marginTop: '4px',
                padding: '8px',
                width: '100%',
                borderRadius: '6px',
                border: '1px solid #cccccc',
              }}
            />
          </label>
          {state === 'idle' || (typeof state === 'object' && state.kind === 'error') ? (
            <button onClick={run_ceremony} style={primary_button_style}>
              {t.register_passkey}
            </button>
          ) : null}
          {state === 'running' ? <p style={{ color: '#545454' }}>{t.waiting}</p> : null}
          {state === 'success' ? <p style={{ color: '#0a7c1f' }}>{t.success}</p> : null}
          {typeof state === 'object' && state.kind === 'error' ? (
            <p role="alert" style={{ color: '#b00020' }}>
              {state.message}
            </p>
          ) : null}
        </>
      }
    />
  );
}

function build_callback_url(return_origin: string, params: Record<string, string>): string {
  const is_https = return_origin.startsWith('https://');
  const base = is_https === true ? return_origin + '/sso/callback' : return_origin;
  const sep = base.indexOf('?') === -1 ? '?' : '&';
  const qs = new URLSearchParams(params).toString();
  return base + sep + qs;
}

const primary_button_style: React.CSSProperties = {
  background: '#BCCF17',
  color: '#1a1a1a',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};
